import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import { ReduxState } from "../../../../redux";
import { setDownloadModalOpen } from "../../../../redux/reducers/JobExport";
import { setSelectedExportType } from "../../../../redux/reducers/DownloadOptions";
import DownloadSection from "./DownloadOptions/DownloadSection";
import { ExportType } from "../../../../docuclipper/DocuclipperTypes";
import { trackFirstExportClicked } from "../../../../utils/activationFunnelAnalytics";

export const DownloadModal = ({ jobType, btnVariant }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { downloadModalOpen } = useSelector(
    (state: ReduxState) => state.JobExport,
  );
  const user = useSelector((state: ReduxState) => state.User.user);
  const subscription = useSelector(
    (state: ReduxState) => state.Subscription.subscription,
  );
  const job = useSelector((state: ReduxState) => state.JobData.job);

  // Track if we opened from URL params so we can clean up on close
  const [openedFromUrl, setOpenedFromUrl] = React.useState(false);

  // Clean up URL params when modal closes (if it was opened from URL)
  const cleanupUrlParams = React.useCallback(() => {
    const params = queryString.parse(location.search);
    if (params.openDownload || params.exportType || params.xeroIntegrationId) {
      const newParams = { ...params };
      delete newParams.openDownload;
      delete newParams.exportType;
      delete newParams.xeroIntegrationId;
      const newSearch = queryString.stringify(newParams);
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });
    }
  }, [location.search, location.pathname, history]);

  const toggle = () => {
    // Capture intent (click) on the way to open; we don't track on close.
    if (!downloadModalOpen) {
      try {
        trackFirstExportClicked({
          jobId: job?.id ?? null,
          userId: (user as any)?.id ?? null,
          jobStatus: job?.status ?? null,
          // Frontend has no reliable "prior exports" count; leave null so the
          // backend/PostHog cohorting can derive it from event history.
          isFirstExportForUser: null,
          planName: subscription?.docuclipperPlan?.name ?? null,
          // pagesInJob is not on Job in Redux; omit rather than guess.
          pagesInJob: null,
        });
      } catch (e) {
        // analytics should never break the UI
      }
    }
    if (downloadModalOpen && openedFromUrl) {
      // Clean up URL params when closing modal that was opened from URL
      cleanupUrlParams();
      setOpenedFromUrl(false);
    }
    dispatch(setDownloadModalOpen(!downloadModalOpen));
  };

  // Check URL params for auto-opening the modal with specific export type
  useEffect(() => {
    const params = queryString.parse(location.search);
    if (params.openDownload === "true" && !downloadModalOpen) {
      dispatch(setDownloadModalOpen(true));
      setOpenedFromUrl(true);

      // Set export type if specified
      if (params.exportType) {
        dispatch(setSelectedExportType(params.exportType as ExportType));
      }

      // Don't clean up URL params here - let child components read them first
      // Cleanup happens when modal is closed
    }
  }, [location.search, dispatch, downloadModalOpen]);

  const { steps } = useSelector((state: ReduxState) => state.OnboardingSteps);

  const animate = steps.filter(
    (s) => s.name === "DOWNLOAD_DATA" && !s.completed,
  );

  return (
    <>
      <Button
        className="btn--download-data mx-2"
        variant={btnVariant}
        onClick={toggle}
        style={
          animate.length > 0
            ? {
                boxShadow: "0 0 15px rgba(0,123,255,0.5)",
                transform: "scale(1.05)",
                transition: "all 0.3s ease",
                fontWeight: "bold",
                animation: "pulse 1.5s infinite",
              }
            : {}
        }
      >
        <style>
          {`
            @keyframes pulse {
              0% {
                box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
              }
              70% {
                box-shadow: 0 0 0 8px rgba(0, 123, 255, 0);
              }
              100% {
                box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
              }
            }
          `}
        </style>
        <FontAwesomeIcon icon="download" />
        <span className="ml-1">Download Data</span>
      </Button>

      <Modal
        size="lg"
        show={downloadModalOpen}
        onHide={() => {
          if (openedFromUrl) {
            cleanupUrlParams();
            setOpenedFromUrl(false);
          }
          dispatch(setDownloadModalOpen(false));
        }}
      >
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder=""
        >
          <Modal.Title>Download your data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DownloadSection jobType={jobType} />
        </Modal.Body>
      </Modal>
    </>
  );
};
